<template>
  <div style="width: 100%">
    <div v-if="withTier" class="tier-selector flex flex--center">
      <el-link
        v-for="tier in TierLabels"
        :key="tier.id"
        type="primary"
        class="tier-label"
        @click="selectTier(tier)"
      >
        {{ tier.label }}
      </el-link>
    </div>
    <el-select
      v-model="value"
      filterable
      collapse-tags
      autocomplete="random"
      :disabled="disabled"
      :placeholder="label"
      :multiple="multiple"
      :size="size"
      popper-class="custom-header"
      :max-collapse-tags="4"
      @update:modelValue="update"
    >
      <template #header>
        <el-checkbox v-model="checkAll" @change="handleCheckAll">
          All
        </el-checkbox>
      </template>
      <el-option
        v-for="item in countries"
        :key="item.iso"
        :label="item.attributes.name"
        :value="useId ? item.id : item.attributes.iso"
      >
        {{ item.attributes.name }}
      </el-option>
    </el-select>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from 'vue'
import { useCountriesStore } from '@/store/modules/countries'
import { TierRange, TierLabel, CountryEntry } from '@/api/types'
import { ICountry } from '@/api/countries'

const TierLabels: Array<TierLabel> = [
  {
    id: TierRange.None,
    label: 'None'
  },
  {
    id: TierRange.Tier1,
    label: 'Tier 1'
  },
  {
    id: TierRange.Tier2,
    label: 'Tier 2'
  },
  {
    id: TierRange.Tier3,
    label: 'Tier 3'
  }
]

export default defineComponent({
  name: 'CountrySelect',
  props: {
    modelValue: {
      required: true,
      type: [String, Array]
    },
    items: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    useId: {
      type: Boolean,
      default: false
    },
    withTier: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'default'
    },
    label: {
      type: String,
      default: 'Choose country'
    }
  },
  setup(props, context) {
    const value = computed(() => props.modelValue)
    const countriesStore = useCountriesStore()
    const checkAll = ref(false)

    const update = (newValue: any) => {
      if (props.multiple) {
        checkAll.value = newValue.length === countries.value.length
        context.emit(
          'update:modelValue',
          newValue.map((x: any) => String(x))
        )
      } else {
        context.emit('update:modelValue', String(newValue))
      }
    }

    const countries = computed((): ICountry[] => {
      return (
        props.items.length > 0 ? props.items : countriesStore.sorted
      ) as ICountry[]
    })

    const selectTier = (tier: TierLabel) => {
      const tierCountries = countries.value
        .filter((c: ICountry) => {
          return c.attributes.tier === tier.id
        })
        .map((c: ICountry) => (props.useId ? c.id : c.attributes.iso))
      const newCountires =
        tierCountries.length > 0 ? [...props.modelValue, ...tierCountries] : []
      const uniqueCountries = [...new Set(newCountires)]
      update(uniqueCountries)
    }

    const handleCheckAll = (val: any) => {
      if (val) {
        update(
          countries.value.map((_) => (props.useId ? _.id : _.attributes.iso))
        )
      } else {
        update([])
      }
    }

    return {
      update,
      countries,
      selectTier,
      TierLabels,
      value,
      handleCheckAll,
      checkAll
    }
  }
})
</script>

<style lang="scss" scoped>
.tier-label {
  margin-right: 2px;
  font-size: 10px;
}
.tier-selector {
  height: 19px;
}
.custom-header {
  .el-checkbox {
    display: flex;
    height: unset;
  }
}
</style>
